import { TFunction } from 'i18next';
import { z } from 'zod';

import {
  areEmailDomainsValid,
  formatEmailDomains,
} from '@bvi/api-interfaces/email-domain';
import {
  AccountStatus,
  PaymentMethod,
} from '@bvi/api-interfaces/entity/client';

export const buildValidationSchema = (t: TFunction) => {
  const dateValue = z
    .number({
      invalid_type_error: t('error.form.required'),
      required_error: t('error.form.required'),
    })
    .or(z.date());

  const requiredText = z.string().min(1, t('error.form.required')).default('');
  const requiredEmail = z
    .string()
    .min(1, t('error.form.required'))
    .email(t('error.form.email'))
    .default('');
  const optionalEmail = z
    .string()
    .email(t('error.form.email'))
    .or(z.literal(''))
    .optional()
    .nullable();

  return z
    .object({
      name: z.string().min(1, t('error.form.required')).max(128).default(''),
      contactPerson: requiredText,
      contactEmail: requiredEmail,
      companySizeId: z.number({
        invalid_type_error: t('error.form.required'),
        required_error: t('error.form.required'),
      }),
      emailDomain: z
        .string()
        .min(1, t('error.form.required'))
        .transform((value) => formatEmailDomains(value))
        .refine((value) => areEmailDomainsValid(value), {
          message: t('error.form.emailDomain'),
        }),
      signatoryName: z.string().optional().nullable(),
      signatoryEmail: optionalEmail,
      billingContactName: z.string().optional().nullable(),
      billingContactEmail: optionalEmail,
      accountManagerName: z.string().optional().nullable(),
      accountManagerEmail: optionalEmail,
      supportLiaisonName: z.string().optional().nullable(),
      supportLiaisonEmail: optionalEmail,
      subscriptionStartsAt: dateValue,
      subscriptionExpiresAt: dateValue,
      subscriptionTermMonths: z.coerce
        .number({
          invalid_type_error: t('error.form.required'),
          required_error: t('error.form.required'),
        })
        .int()
        .min(1, t('error.form.required')),
      orientationAt: z.union([z.number(), z.date()]).optional().nullable(),
      paymentMethod: z.nativeEnum(PaymentMethod).optional().nullable(),
      paymentMethodNotes: z.string().optional().nullable(),
      accountStatus: z.nativeEnum(AccountStatus, {
        required_error: t('error.form.required'),
        invalid_type_error: t('error.form.required'),
      }),
      pricingNarrative: z.string().optional().nullable(),
    })
    .refine(
      (values) =>
        new Date(values.subscriptionStartsAt).getTime() <=
        new Date(values.subscriptionExpiresAt).getTime(),
      {
        message: t('error.form.startBeforeEnd'),
        path: ['subscriptionExpiresAt'],
      },
    )
    .refine(
      (values) =>
        values.paymentMethod !== PaymentMethod.Other ||
        Boolean(values.paymentMethodNotes?.trim()),
      {
        message: t('error.form.required'),
        path: ['paymentMethodNotes'],
      },
    );
};
